/**
 * 主題偏好（淺色 / 深色 / 跟隨系統）的純邏輯層。
 *
 * 刻意**不放進 zustand 的 project-store**：主題是「使用者偏好」而非專案資料，
 * 不進 undo 歷史、不標 dirty、也不寫進 `.bproj`（否則把檔案給別人，對方開檔
 * 就會被我的偏好覆蓋）。持久化一律走 `localStorage`。
 *
 * DOM 套用方式：在 `<html>` 上 toggle `dark` class。Tailwind v4 預設的
 * dark variant 綁的是 `prefers-color-scheme`，因此 `src/styles.css` 另外用
 * `@custom-variant dark (&:where(.dark, .dark *))` 改成 class 策略——所有
 * 既有的 `dark:` 樣式因此改由這個 class 驅動，元件本身不需要任何改動。
 */

export type ThemePreference = 'light' | 'dark' | 'system';

/** `system` 解析後的實際主題。 */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'bigint-project.theme';

export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * 讀取已儲存的偏好。
 *
 * 讀不到、值不合法、或 `localStorage` 不可用（隱私模式、測試環境）時一律
 * 回退預設值——主題壞掉不該讓整個 app 起不來。
 */
export function readStoredThemePreference(): ThemePreference {
  try {
    const raw = globalThis.localStorage?.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function writeStoredThemePreference(preference: ThemePreference): void {
  try {
    globalThis.localStorage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // localStorage 寫入失敗（配額、隱私模式）不影響本次 session 的主題套用。
  }
}

function matchDarkQuery(): MediaQueryList | null {
  if (typeof globalThis.matchMedia !== 'function') return null;
  return globalThis.matchMedia(DARK_QUERY);
}

/** 系統目前是否為深色。取不到 `matchMedia`（jsdom 預設）時視為淺色。 */
export function prefersDarkSystemTheme(): boolean {
  return matchDarkQuery()?.matches ?? false;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return prefersDarkSystemTheme() ? 'dark' : 'light';
  return preference;
}

/** 把解析後的主題套到 `<html>`。無 DOM 環境（SSR／node）時靜默略過。 */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = globalThis.document?.documentElement;
  if (root === undefined || root === null) return;
  root.classList.toggle('dark', resolved === 'dark');
  root.dataset.theme = resolved;
}

/**
 * 監聽系統主題變化。回傳解除監聽的函式。
 *
 * 只有 `system` 模式需要它——但這裡不判斷模式，由 theme-store 決定何時掛載，
 * 讓這個函式維持單一職責、好測。
 */
export function watchSystemTheme(onChange: (isDark: boolean) => void): () => void {
  const query = matchDarkQuery();
  if (query === null) return () => undefined;
  const handler = (event: MediaQueryListEvent): void => onChange(event.matches);
  // Safari < 14 只有 addListener；Tauri 的 WKWebView 版本雖然夠新，
  // 但測試 mock 也常只提供其中一組，兩種都接受比較穩。
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }
  if (typeof query.addListener === 'function') {
    query.addListener(handler);
    return () => query.removeListener(handler);
  }
  return () => undefined;
}
